import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { authApi } from '../api';
import { relativeTime } from '@/lib/datetime';

export function SessionsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: authApi.listSessions,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] }),
  });

  const revokeAll = useMutation({
    mutationFn: () => authApi.logoutAll(),
    onSuccess: () => {
      // Triggers the 401-on-next-request → forced re-login path.
      window.location.assign('/login');
    },
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Active sessions</h1>
          <p className="text-sm text-muted-foreground">
            Sessions are device + workspace pairs. Revoke any you don't recognize.
          </p>
        </div>
        <button
          onClick={() => revokeAll.mutate()}
          disabled={revokeAll.isPending}
          className="btn-danger"
        >
          {revokeAll.isPending ? 'Working…' : 'Log out of everything'}
        </button>
      </header>

      <div className="card overflow-hidden !p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Device</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Last used</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : (data ?? []).length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  No active sessions.
                </td>
              </tr>
            ) : (
              data!.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    {s.device_label || s.user_agent?.slice(0, 60) || 'Unknown device'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.ip_address ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {s.last_used_at ? relativeTime(s.last_used_at) : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{relativeTime(s.expires_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => revoke.mutate(s.id)}
                      disabled={revoke.isPending}
                      className="text-sm text-danger hover:underline"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
